import { useState } from "react";

import type { Question } from "../api/client";

type Props = {
  questions: Question[];
  onComplete: (answers: number[]) => void;
};

export function AssessmentQuiz({ questions, onComplete }: Props) {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);

  // Nothing to ask. Unreachable from AssessmentPage today — the "intro" state
  // always carries the full 15-question set from the QUESTIONS constant — but
  // the prop type permits an empty list, and without this the render below
  // reads `question.prompt` off `undefined` and divides by zero for `progress`
  // (Infinity, which lands in a `scaleX()` as invalid CSS). Render nothing
  // rather than a half-drawn quiz.
  if (questions.length === 0) return null;

  const question = questions[index];
  const progress = ((index + 1) / questions.length) * 100;

  const choose = (optionIndex: number) => {
    const next = [...answers, optionIndex];
    if (next.length === questions.length) {
      onComplete(next);
      return;
    }
    setAnswers(next);
    setIndex(index + 1);
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-12">
      <div className="wren-eyebrow mb-4 text-center text-[0.75rem]">
        {index + 1} of {questions.length}
      </div>
      <div
        role="progressbar"
        aria-label="Quiz progress"
        aria-valuenow={index + 1}
        aria-valuemin={1}
        aria-valuemax={questions.length}
        className="mb-12 h-1 rounded-full bg-[color:var(--color-wren-border-subtle)]"
      >
        <div
          className="h-1 rounded-full bg-[color:var(--color-wren-green)] transition-transform duration-300 ease-out"
          style={{
            transform: `scaleX(${progress / 100})`,
            transformOrigin: "left",
            width: "100%",
          }}
        />
      </div>
      <h2 className="mb-12 text-center text-[2rem] leading-[100%]">
        {question.prompt}
      </h2>
      <div className="flex flex-col gap-4">
        {question.options.map((o, i) => (
          <button
            key={i}
            onClick={() => choose(i)}
            className="border border-[color:var(--color-wren-border)] bg-white p-6 text-left text-[1rem] leading-[1.5rem] text-[color:var(--color-wren-gray-700)] transition-colors duration-150 hover:border-[color:var(--color-wren-green)] hover:bg-[color:var(--color-wren-border-subtle)] active:border-[color:var(--color-wren-green-dark)] active:bg-[color:var(--color-wren-light-primary)]"
            style={{
              borderRadius: "var(--radius-wren-card)",
              boxShadow: "var(--shadow-wren-button)",
            }}
          >
            {o.text}
          </button>
        ))}
      </div>
    </div>
  );
}
